import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import {
  HiOutlineFolder,
  HiOutlineCheckCircle,
  HiOutlineUser,
  HiOutlineEnvelope,
  HiOutlineCamera,
  HiOutlineBuildingOffice2,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineClipboard,
} from "react-icons/hi2";
import { authFetch } from "../../HttpClient";
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";
import { MODULE_KEYS, getFunctionalityIcon, defaultPoleForUser, POLE_LABELS } from "./moduleIcons.js";
import { useClients } from "../Portefeuille/useClients.js";

const CLIENT_OWNER_FIELDS = [
  "id_expertComptable",
  "id_superviseur",
  "id_collaborateur",
  "id_collaborateur_niv2",
  "id_social",
  "id_juridique",
];

const EDIT_FORM_ID = "user-edit-form";

const inputClass =
  "w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]";
const labelClass = "text-xs font-medium text-gray-500";

function UserDetailModal({ user, onClose, onUserUpdated }) {
  const { data: clients = [] } = useClients();
  const [activeTab, setActiveTab] = useState("profil");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState(null);
  const fileInputRef = useRef(null);

  // Mot de passe (en clair côté base) : réservé au module Admin, jamais
  // inclus dans la liste globale des utilisateurs (voir EtatGlobal.js) — on
  // le récupère à part via GET /api/users/:id, protégé par requireAdminModule.
  const [password, setPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    authFetch(`https://backend-myalfa.vercel.app/api/users/${user.id}`)
      .then((res) => res.json())
      .then((data) => setPassword(data.password ?? ""))
      .catch((err) => console.error("Erreur récupération mot de passe :", err));
  }, [user?.id]);

  const handleCopyPassword = () => {
    if (!password) return;
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const dossiersGeres = useMemo(() => {
    if (!user) return 0;
    return clients.filter((c) =>
      CLIENT_OWNER_FIELDS.some((field) => c[field] === user.id)
    ).length;
  }, [clients, user]);

  const activeModulesCount = useMemo(() => {
    if (!user) return 0;
    return MODULE_KEYS.filter((key) =>
      Array.isArray(user.fonctionalite)
        ? user.fonctionalite.includes(key)
        : user.fonctionalite === key
    ).length;
  }, [user]);

  if (!user) return null;

  const initials = `${user.name?.[0] || ""}${user.lastName?.[0] || ""}`;
  const primaryCabinet = user.userCabinets?.[0]?.cabinet;
  const isActive = (user.status || "Actif") === "Actif";

  const handlePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Upload direct navigateur → Blob (contourne la limite 4,5 Mo), avec
      // une progression affichée pendant l'envoi.
      const pictureUrl = await uploadFileToBlob(
        file,
        "https://backend-myalfa.vercel.app/api/users/upload-token",
        ({ percentage }) => setUploadProgress(Math.round(percentage))
      );
      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/users/${user.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pictureUrl }),
        }
      );
      const updatedUser = await response.json();
      onUserUpdated(updatedUser);
    } catch (err) {
      console.error("Erreur lors de l'envoi de la photo :", err);
    } finally {
      setUploadProgress(null);
      setUploading(false);
    }
  };

  const startEditing = () => {
    setForm({
      civilite: user.civilite || "Monsieur",
      name: user.name || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
      poste: user.poste || "",
      role: user.role,
      pole: user.pole || defaultPoleForUser(user),
      password: "",
      fonctionalite: user.fonctionalite || [],
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setForm(null);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleModule = (item) => {
    if (!isEditing) return;
    setForm((prev) => {
      const has = prev.fonctionalite.includes(item);
      return {
        ...prev,
        fonctionalite: has
          ? prev.fonctionalite.filter((f) => f !== item)
          : [...prev.fonctionalite, item],
      };
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    setIsSaving(true);

    const formData = new FormData();
    formData.append("civilite", form.civilite);
    formData.append("name", form.name);
    formData.append("lastName", form.lastName);
    formData.append("email", form.email);
    formData.append("phone", form.phone);
    formData.append("poste", form.poste);
    if (form.password) {
      formData.append("password", form.password);
    }
    formData.append("role", form.role);
    formData.append("pole", form.pole);
    formData.append("fonctionalite", JSON.stringify(form.fonctionalite));

    authFetch(`https://backend-myalfa.vercel.app/api/users/${user.id}`, {
      method: "PUT",
      body: formData,
    })
      .then((response) => response.json())
      .then((updatedUser) => {
        onUserUpdated(updatedUser);
        setIsEditing(false);
        setForm(null);
      })
      .catch((err) => console.error("Erreur lors de la mise à jour :", err))
      .finally(() => setIsSaving(false));
  };

  const Avatar = ({ size }) => (
    <div
      className={`${size} rounded-xl bg-[#5C1A30] flex items-center justify-center text-white font-bold uppercase overflow-hidden flex-shrink-0`}
    >
      {user.picture ? (
        <img src={user.picture} alt="" className="w-full h-full object-cover" />
      ) : (
        initials
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black/45 z-[1000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-7 py-6 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <Avatar size="w-14 h-14 text-lg" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#840040] transition-colors"
              >
                <HiOutlineCamera size={13} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                hidden
                onChange={handlePictureChange}
              />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {user.lastName} {user.name}
              </h2>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {user.status || "Actif"}
                </span>
                <span>{user.poste || user.role}</span>
                {primaryCabinet && (
                  <>
                    <span>·</span>
                    <span>{primaryCabinet.name}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="px-4 py-2 rounded-xl border border-[#840040] text-[#840040] text-sm font-semibold hover:bg-[#840040]/5 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  form={EDIT_FORM_ID}
                  disabled={isSaving}
                  className="px-4 py-2 rounded-xl bg-[#5C1A30] text-white text-sm font-semibold hover:bg-[#4a1526] transition-colors disabled:opacity-60"
                >
                  {isSaving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </>
            ) : (
              <button
                onClick={startEditing}
                className="px-4 py-2 rounded-xl bg-[#5C1A30] text-white text-sm font-semibold hover:bg-[#4a1526] transition-colors"
              >
                Modifier
              </button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
            >
              <IoClose size={18} />
            </button>
          </div>
        </div>

        <div className="px-7 py-6">
          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="flex items-center gap-4 border border-gray-100 rounded-2xl px-5 py-4">
              <div className="w-11 h-11 rounded-xl bg-[#f5ede8] flex items-center justify-center flex-shrink-0">
                <HiOutlineFolder size={20} className="text-[#5C1A30]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{dossiersGeres}</p>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Dossiers gérés
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 border border-gray-100 rounded-2xl px-5 py-4">
              <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <HiOutlineCheckCircle size={20} className="text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {activeModulesCount}
                  <span className="text-sm text-gray-400 font-medium">
                    /{MODULE_KEYS.length}
                  </span>
                </p>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Modules autorisés
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex bg-[#fff6ee] rounded-xl p-1 mb-5">
            <button
              type="button"
              onClick={() => setActiveTab("profil")}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === "profil" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
              }`}
            >
              Profil
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("modules")}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === "modules" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
              }`}
            >
              Droits aux modules
            </button>
          </div>

          <form id={EDIT_FORM_ID} onSubmit={handleSave}>
            {activeTab === "profil" ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-gray-100 rounded-2xl p-5">
                  <div className="flex items-center gap-2 pb-3 mb-3 border-b border-gray-100">
                    <HiOutlineUser size={16} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900">Identité</h3>
                  </div>

                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Civilité</label>
                        <select
                          value={form.civilite}
                          onChange={(e) => handleFormChange("civilite", e.target.value)}
                          className={inputClass}
                        >
                          <option value="Monsieur">Monsieur</option>
                          <option value="Madame">Madame</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Profil *</label>
                        <select
                          value={form.role}
                          onChange={(e) => handleFormChange("role", e.target.value)}
                          required
                          className={inputClass}
                        >
                          <option value="EXPERT_COMPTABLE">Expert comptable</option>
                          <option value="SUPERVISEUR">Superviseur</option>
                          <option value="COLLABORATEUR">Collaborateur</option>
                          <option value="ASSISTANT">Assistant</option>
                          <option value="ADMINISTRATEUR">Administrateur</option>
                          <option value="SECRETARIAT">Secrétariat</option>
                          <option value="SOCIAL">Social</option>
                          <option value="JURIDIQUE">Juridique</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Pôle * (bureau affiché)</label>
                        <select
                          value={form.pole}
                          onChange={(e) => handleFormChange("pole", e.target.value)}
                          required
                          className={inputClass}
                        >
                          <option value="COMPTABILITE">Comptabilité</option>
                          <option value="SOCIAL">Social</option>
                          <option value="SECRETARIAT">Secrétariat</option>
                          <option value="JURIDIQUE">Juridique</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Nom *</label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => handleFormChange("name", e.target.value)}
                          required
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Prénom *</label>
                        <input
                          type="text"
                          value={form.lastName}
                          onChange={(e) => handleFormChange("lastName", e.target.value)}
                          required
                          className={inputClass}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className={labelClass}>Fonction *</label>
                        <input
                          type="text"
                          value={form.poste}
                          onChange={(e) => handleFormChange("poste", e.target.value)}
                          required
                          className={inputClass}
                        />
                      </div>
                    </div>
                  ) : (
                    <dl className="space-y-2.5 text-sm">
                      <div className="flex items-center justify-between">
                        <dt className="text-gray-400">Code</dt>
                        <dd className="font-medium text-gray-800 uppercase">{initials}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-gray-400">Nom</dt>
                        <dd className="font-medium text-gray-800 uppercase">{user.name}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-gray-400">Prénom</dt>
                        <dd className="font-medium text-gray-800">{user.lastName}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-gray-400">Profil</dt>
                        <dd className="font-medium text-gray-800">
                          {user.poste || user.role}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-gray-400">Pôle (bureau affiché)</dt>
                        <dd className="font-medium text-gray-800">
                          {POLE_LABELS[user.pole || defaultPoleForUser(user)]}
                        </dd>
                      </div>
                    </dl>
                  )}

                  <div className="mt-4 bg-[#fff6ee] rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-600 mb-3">Photo de profil</p>
                    <div className="flex items-center gap-3">
                      <Avatar size="w-14 h-14" />
                      <div>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <HiOutlineCamera size={14} />
                          {uploading ? (uploadProgress !== null ? `Envoi... ${uploadProgress}%` : "Envoi...") : "Importer"}
                        </button>
                        <p className="text-[11px] text-gray-400 mt-1.5">JPG ou PNG</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-100 rounded-2xl p-5">
                  <div className="flex items-center gap-2 pb-3 mb-3 border-b border-gray-100">
                    <HiOutlineEnvelope size={16} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900">Coordonnées</h3>
                  </div>

                  {isEditing ? (
                    <div className="flex flex-col gap-3">
                      <div>
                        <label className={labelClass}>Email *</label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => handleFormChange("email", e.target.value)}
                          required
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Téléphone</label>
                        <input
                          type="text"
                          value={form.phone}
                          onChange={(e) => handleFormChange("phone", e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Nouveau mot de passe *</label>
                        <input
                          type="password"
                          value={form.password}
                          onChange={(e) => handleFormChange("password", e.target.value)}
                          required
                          className={inputClass}
                        />
                      </div>
                    </div>
                  ) : (
                    <dl className="space-y-2.5 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-gray-400 flex-shrink-0">Mail professionnel</dt>
                        <dd className="font-medium text-gray-800 text-right truncate">
                          {user.email}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-gray-400">Ligne directe</dt>
                        <dd className="font-medium text-gray-800">{user.phone || "—"}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-gray-400 flex-shrink-0">Mot de passe</dt>
                        <dd className="font-medium text-gray-800 flex items-center gap-1.5">
                          <span className="font-mono tracking-wide">
                            {password === null
                              ? "…"
                              : showPassword
                              ? password || "—"
                              : "••••••••"}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            title={showPassword ? "Masquer" : "Afficher"}
                            className="text-gray-400 hover:text-[#840040] p-0.5 transition-colors"
                          >
                            {showPassword ? (
                              <HiOutlineEyeSlash size={14} />
                            ) : (
                              <HiOutlineEye size={14} />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={handleCopyPassword}
                            disabled={!password}
                            title="Copier"
                            className="text-gray-400 hover:text-[#840040] p-0.5 transition-colors disabled:opacity-40"
                          >
                            <HiOutlineClipboard size={14} />
                          </button>
                          {copied && (
                            <span className="text-[10px] font-semibold text-emerald-600">
                              Copié
                            </span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  )}

                  {primaryCabinet && (
                    <div className="mt-4 bg-[#fff6ee] rounded-xl p-4">
                      <p className="text-xs font-semibold text-gray-600 mb-3">Rattachement</p>
                      <dl className="space-y-2 text-sm mb-3">
                        <div className="flex items-center justify-between">
                          <dt className="text-gray-400">Cabinet</dt>
                          <dd className="font-medium text-gray-800">{primaryCabinet.name}</dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt className="text-gray-400">Ville</dt>
                          <dd className="font-medium text-gray-800">
                            {primaryCabinet.city || "—"}
                          </dd>
                        </div>
                      </dl>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5C1A30] text-white text-xs font-semibold">
                        <HiOutlineBuildingOffice2 size={13} />
                        {primaryCabinet.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="border border-gray-100 rounded-2xl p-5">
                <div className="grid grid-cols-2 gap-2.5">
                  {MODULE_KEYS.map((key) => {
                    const active = isEditing
                      ? form.fonctionalite.includes(key)
                      : Array.isArray(user.fonctionalite)
                      ? user.fonctionalite.includes(key)
                      : user.fonctionalite === key;
                    return (
                      <div
                        key={key}
                        onClick={() => handleToggleModule(key)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                          active ? "bg-[#5C1A30]" : "bg-[#f5ede8]"
                        } ${isEditing ? "cursor-pointer" : ""}`}
                      >
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-black/10">
                          {getFunctionalityIcon(key, active)}
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            active ? "text-white" : "text-gray-400"
                          }`}
                        >
                          {key}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default UserDetailModal;
